import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import {
  CategoryFullWithSubcatSubgroup,
  NoteService,
  NotePostBody,
  NotePutBody,
  Subcategory,
  TaxReturnService,
  ListOfNotesNotesInner
} from "../../../../swaggerServices";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DropdownListOption } from "../../../shared/components/search-dropdown/search-dropdown.component";
import { Subscription } from "rxjs";
import { TaxYearsService } from "../../../shared/util/tax-years.service";
import { TaxReturnTypeSubTypeService } from "../../../shared/util/tax-return-type-subtype.service";
import {
  ITaxReturn
} from "../../../app/tax-returns/tax-return/model/i-tax-return";
import moment from "moment";
import { cloneDeep } from "lodash-es";
import { IPopulation } from "../../../app/clients/population-taxpayers/population-taxpayers.component";
import {
  getFullTaxReturn,
  resolveDocuments
} from "../../../app/tax-returns/get-full-tax-return";
import {
  findUrls,
  handleUrls,
  parseMessageForDocumentsAndUrls,
  Parts,
  AttachmentRef
} from "../../../app/tax-returns/tax-return/urls";
import { PastedDocMetadata } from "../../../app/tax-returns/tax-return/subcategory/subcategory";
import { IAssignee } from "../../types/assignee";
import { ConfigStateService } from "../../../app/config/config-state.service";
import { TenantConfigService } from "../../services/tenant-config.service";
import { TranslocoService } from "@jsverse/transloco";
import { Folders, initFolders, AttachmentsService } from "../../../app/tax-returns/tax-return/subcategory/comments/attachments/attachments.service";

declare var $: any;

export interface INote extends ListOfNotesNotesInner {
  startYear?: number;
  subcategories?: Subcategory[];
  subcategoryIds?: number[]; // only used for saving
  updatedAt?: string;
  createdAt?: string;
  updatedAtString?: string;
  createdAtString?: string;
  parts?: Parts;
  noteAudits?: any[];
  updatedBy?: string;
  createdBy?: string;
}

type NoteAttachmentValue = string | AttachmentRef;

@Component({
    selector: "app-notes",
    templateUrl: "./notes.component.html",
    styleUrls: ["./notes.component.scss"],
    standalone: false
})
export class NotesComponent implements OnInit, OnDestroy {
  @Input() assignee: IAssignee;
  @Input() taxReturn?: ITaxReturn;
  @Input() population?: IPopulation;
  @Input() subcategoryId?: number; // filter notes by sc
  @Input() setModal?: (src: string) => void;
  isLoading?: boolean;
  searchForm: UntypedFormGroup = new UntypedFormGroup({});
  taxYear: UntypedFormControl;
  taxYearOptions: DropdownListOption[];
  subscriptions: Subscription[] = [];
  note: INote;
  noteControl: UntypedFormControl;
  noteOriginal: INote;
  showNoteForm: boolean;
  notes: INote[];
  uiConfig: TenantConfigService;
  taxReturnAlias: string;
  yearLabel: string;
  notesOriginal: INote[];
  @ViewChild("multiSelectDropdown") multiSelectDropdown: ElementRef;
  @Output() noteAddedEmit: EventEmitter<number> = new EventEmitter<number>();
  commentDocumentMetadatas: PastedDocMetadata[] = [];
  categories?: CategoryFullWithSubcatSubgroup[];
  folders: Folders = initFolders();
  useAttachments: boolean = false;
  modalImgSrc: string = "";

  private toAttachmentRef(value: NoteAttachmentValue, fallbackName?: string): AttachmentRef {
    if (typeof value === "string") {
      const fileName = fallbackName || value.split(/[\\/]/).pop() || value;
      const extension = (fileName.split(".").pop() || "").toLowerCase();
      return {
        id: value,
        fileName,
        extension,
      };
    }

    const fileName = value.fileName || fallbackName;
    const extension = value.extension || (fileName ? (fileName.split(".").pop() || "").toLowerCase() : "");
    return {
      id: value.id,
      fileName,
      extension,
    };
  }

  private buildAttachmentMap(note: INote): Map<string, AttachmentRef> {
    const savedAttachments = (note.parts?.attachments || []).map((attachment: NoteAttachmentValue) => this.toAttachmentRef(attachment));
    const imageAttachments = (note.parts?.parts || [])
      .filter((part): part is { image: string; imageName?: string } => typeof part.image === "string" && !!part.image)
      .map(part => this.toAttachmentRef(part.image, part.imageName));
    const existingAttachments = [...savedAttachments, ...imageAttachments];

    return new Map(
      existingAttachments.map(attachment => [attachment.fileName || attachment.id, attachment])
    );
  }

  private isStoredAttachmentValue(value: unknown): value is NoteAttachmentValue {
    if (typeof value === "string") return true;
    if (!value || typeof value !== "object") return false;
    return typeof (value as AttachmentRef).id === "string";
  }

  constructor(
    private noteService: NoteService,
    private taxYearsService: TaxYearsService,
    private taxReturnService: TaxReturnService,
    private taxReturnTypeSubTypeService: TaxReturnTypeSubTypeService,
    private config: ConfigStateService,
    private translocoService: TranslocoService,
    private attachmentsService: AttachmentsService,
  ) {}

  ngOnInit() {
    this.useAttachments = Boolean(this.taxReturn);
    if (this.useAttachments) {
      this.attachmentsService.setRootFolder(this.folders, this.taxReturn.docshareRootFolder);
    }
    this.noteControl = new UntypedFormControl({ message: "", attachments: new Map() })
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService
          .selectTranslate("year")
          .subscribe(value => (this.yearLabel = value));
        this.translocoService
          .selectTranslate("tax-return-alias")
          .subscribe(value => (this.taxReturnAlias = value));
      })
    );

    const taxYrsSub = () => {
      this.subscriptions.push(
        this.taxYearsService.getTaxYears().subscribe((years: any) => {
          this.taxYearOptions = years;
          let taxYr; // default to the pop/tax return yr
          if (this.taxReturn) taxYr = this.taxReturn.year;
          else if (this.population) taxYr = this.population.year;
          this.taxYear = new UntypedFormControl(taxYr);
          this.searchForm = new UntypedFormGroup({});
          this.searchForm.addControl("taxYear", this.taxYear);
          this.subscriptions.push(
            this.taxYear.valueChanges.subscribe(() =>
              this.initNotes(this.taxYear.value.value)
            )
          );
        })
      );
    };
    this.isLoading = true;

    // when showing notes from the population taxpayers page, need to get full tax-return to fetch sc and notes data
    if (this.taxReturn) {
      if (!this.taxReturn.notes || !this.taxReturn.categories) {
        this.subscriptions.push(
          getFullTaxReturn(
            this.taxReturnService,
            this.taxReturn.id,
            null
          ).subscribe(
            (taxReturn: ITaxReturn) => {
              this.taxReturn = taxReturn;
              this.categories = taxReturn.categories;
              this.notes = this.taxReturn.notes;
              this.notesOriginal = cloneDeep(this.notes);
              this.initNotes(this.taxReturn.year);
              taxYrsSub();
            },
            () => (this.isLoading = false)
          )
        );
      } else {
        this.categories = this.taxReturn.categories;
        this.notes = this.taxReturn.notes;
        this.notesOriginal = cloneDeep(this.notes);
        this.initNotes(this.taxReturn.year);
        taxYrsSub();
      }
    } else if (this.population) {
      this.notes = this.population.notes;
      this.notesOriginal = cloneDeep(this.notes);
      this.initNotes(this.population.year);

      const ALL_TYPE_CATEGORY_SELECTOR = -1;
      this.taxReturnService
        .getTaxReturnStructure(this.population.year, ALL_TYPE_CATEGORY_SELECTOR)
        .subscribe((categories: any) => {
          // need to fetch all of the tax return types for populations as notes can be selected on any of the subcategories across types
          this.taxReturnTypeSubTypeService
            .getTaxReturnTypes()
            .subscribe(types => {
              // if there is only 1 type and subtype dont worry about prefixing the dropdown option
              if (types.length === 1 && types[0].subtypeCount === 1) {
                this.categories = categories;
              } else {
                // set the dropdown option to also include the taxreturn type name in the subcategory
                this.categories = categories;
                this.categories.forEach(c => {
                  const taxReturnTypeName = types.find(
                    t => t.id === c.taxReturnTypeId
                  ).name;
                  c.subcategories.forEach(
                    sc => (sc.name = `${taxReturnTypeName} - ${sc.name}`)
                  );
                });
              }
              taxYrsSub();
            });
        });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  initNotes(year?: number) {
    this.isLoading = false;
    this.notes = this.notesOriginal.filter(
      n => n.status === 0 && year <= n.endYear && year >= n.startYear
    );
    // show assignee notes first to allow auto-select when selecting between tax years
    this.notes = this.notes
      .filter(n => n.assigneeId != null && n.populationId == null)
      .concat(
        this.notes.filter(n => n.assigneeId == null && n.populationId != null)
      );

    if (this.subcategoryId)
      this.notes = this.notes.filter(n =>
        n.subcategories.some(sc => Number(sc.id) === Number(this.subcategoryId))
      );

    this.selectNote(this.notes[0] || {});
    // update notes count on subcategory page
    this.noteAddedEmit.emit(this.notes.length);
  }

  selectNote(note: INote) {
    this.commentDocumentMetadatas = [];
    if (note.id) {
      note.createdAtString = moment(note.createdAt).format("DD/MM/YYYY");
      note.updatedAtString = moment(note.updatedAt).format("DD/MM/YYYY");
      note.updatedBy = `${
        note.noteAudits[note.noteAudits.length - 1].user.firstName
      } ${note.noteAudits[note.noteAudits.length - 1].user.lastName}`;
      note.createdBy = `${note.noteAudits[0].user.firstName} ${note.noteAudits[0].user.lastName}`;
      note.parts = parseMessageForDocumentsAndUrls(note.body, note.attachments);
      if (note.parts.documents) this.resolveDocuments(note);
    }
    this.note = note;
    this.showNoteForm = false;
  }

  newNote() {
    this.note = {
      id: null,
      title: null,
      body: null,
      endYear: null,
      startYear: null,
      assigneeId: null,
      populationId: null,
      subcategories: [],
      subcategoryIds: [],
      status: 0
    };
    this.noteControl.patchValue({ message: "", attachments: new Map() })

    if (this.taxReturn) {
      this.note.startYear = this.taxReturn.year;
      this.note.endYear = this.taxReturn.year;
      this.note.assigneeId = this.taxReturn.assigneeId;
    } else if (this.population) {
      this.note.startYear = this.population.year;
      this.note.endYear = this.population.year;
      this.note.populationId = this.population.id;
    }

    this.showNoteForm = true;

    // sc selection only applies to tax return notes
    if (this.categories) {
      setTimeout(() => {
        $(this.multiSelectDropdown.nativeElement).dropdown({
          placeholder: "Select",
          preserveHTML: false,
          onChange: value => (this.note.subcategoryIds = value)
        });
        // when creating new note from subcategory page, auto select the subcategory
        if (this.subcategoryId) {
          $(this.multiSelectDropdown.nativeElement).dropdown(
            "set selected",
            String(this.subcategoryId)
          );
        }
      }, 0);
    }
  }

  editNote(note: INote) {
    this.note = cloneDeep(note);
    const attachments = this.buildAttachmentMap(this.note);
    this.noteControl.patchValue({ message: this.note.body, attachments });
    this.noteOriginal = cloneDeep(note);
    this.showNoteForm = true;
    // sc selection only applies to tax return notes
    if (this.categories) {
      setTimeout(() => {
        $(this.multiSelectDropdown.nativeElement).dropdown({
          placeholder: "Select...",
          preserveHTML: false,
          onChange: value => (this.note.subcategoryIds = value)
        });
        $(this.multiSelectDropdown.nativeElement).dropdown(
          "set selected",
          this.note.subcategories.map(sc => String(sc.id))
        );
      }, 0);
    }
  }

  deleteNote(note: INote) {
    if (confirm("Are you sure you want to delete this note?")) {
      note.status = 99;
      this.saveNote();
    }
  }

  resetNoteForm() {
    this.showNoteForm = false;
    this.note = this.noteOriginal;
    const attachments = this.buildAttachmentMap(this.note);
    this.noteControl.patchValue({ message: this.note.body, attachments });
    // when cancelling a new note, auto-select first note in list
    if ((!this.note || !this.note.id) && this.notes[0])
      this.selectNote(this.notes[0]);
  }

  async saveNote() {
    const allAttachments: unknown[] = Array.from(this.noteControl.value.attachments.values());
    const existingAttachments: NoteAttachmentValue[] = allAttachments
      .filter(this.isStoredAttachmentValue);
    const newAttachments: File[] = allAttachments
      .filter((attachment): attachment is File => attachment instanceof File);
    const { attachments: uploadedAttachments, success } = this.useAttachments
      ? await this.attachmentsService.uploadAttachments(this.folders, "Notes", newAttachments)
      : { attachments: [], success: true };
    if (!success) return;
    const currentAttachments: AttachmentRef[] = [...uploadedAttachments, ...existingAttachments].map(attachment =>
      this.toAttachmentRef(attachment)
    );
    const reqBase = {
      title: this.note.title,
      body: this.noteControl.value.message,
      attachments: currentAttachments,
      endYear: this.note.endYear,
      startYear: this.note.startYear,
      assigneeId: this.note.assigneeId,
      populationId: this.note.populationId,
      subcategoryIds: this.note.subcategoryIds,
      status: this.note.status
    };

    if (!this.note.id) {
      const req: NotePostBody = reqBase;
      this.noteService.addNote(req).subscribe((res: any) => {
        // get the subcategories and user details
        this.noteService.getNoteById(res.id).subscribe((note: INote) => {
          this.notes.push(note);
          this.notesOriginal.push(note);
          if (this.taxReturn) this.initNotes(this.taxYear.value.value);
          else if (this.population) this.initNotes(this.taxYear.value.value);
          this.selectNote(note);
        });
      });
    } else {
      const req: NotePutBody = { ...reqBase, id: this.note.id };
      this.noteService.updateNote(req).subscribe((res: any) => {
        // get the subcategories and user details
        this.noteService.getNoteById(res.id).subscribe((note: INote) => {
          let noteIndex = this.notes.findIndex(n => n.id === this.note.id);
          this.notes[noteIndex] = note;
          let noteOriginalIndex = this.notesOriginal.findIndex(
            n => n.id === this.note.id
          );
          this.notesOriginal[noteOriginalIndex] = note;
          if (this.taxReturn) this.initNotes(this.taxYear.value.value);
          else if (this.population) this.initNotes(this.taxYear.value.value);
          if (note.status === 0) {
            // when on sc page and removing the sc from the note
            if (
              this.subcategoryId &&
              note.subcategories &&
              !note.subcategories.find(sc => sc.id === this.subcategoryId)
            )
              this.selectNote(this.notes[0] || {});
            else this.selectNote(note);
          }
          // when deleting a note
          else {
            this.selectNote(this.notes[0] || {});
          }
        });
      });
    }
  }

  hasAssigneeNotes() {
    return this.notes && this.notes.some(n => Boolean(n.assigneeId));
  }

  hasPopulatioNotes() {
    return this.notes && this.notes.some(n => Boolean(n.populationId));
  }

  resolveDocuments(note) {
    if (note.parts.documents && note.parts.documents.length) {
      resolveDocuments(this.taxReturnService, note.parts).subscribe(() => {});
    }
  }

  // display docshare files whenever the docshare url is pasted in
  handleUrls() {
    const urls = findUrls(this.noteControl.value.message).map(({ url }) => url);
    handleUrls(urls, this.commentDocumentMetadatas, this.taxReturnService).then(
      (data: PastedDocMetadata[]) => (this.commentDocumentMetadatas = data)
    );
  }

  setNoteModal(src?: string) {
    if (this.setModal) {
      this.setModal(src);
    } else {
      this.modalImgSrc = src ?? null;
    }
  }
}
